import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TextState, VoiceSessionContext, SpacingResult } from '../types/dictation.types';
import { LoggerService } from './logger.service';

/**
 * Redesigned Text State Manager for Streaming Voice Integration
 *
 * This version correctly handles the server's behavior of sending final segments
 * during dictation and a full accumulated transcript at the end. It maintains a
 * client-side state by accumulating final segments and displaying interim results.
 */
@Injectable({
  providedIn: 'root',
})
export class TextStateManager {
  private nativeElement: HTMLTextAreaElement | HTMLInputElement | null = null;
  private formControl: FormControl | null = null;

  // Session state
  private voiceSessionActive = false;
  private sessionStartState: TextState | null = null;

  // Text components for assembly
  private textBeforeStart = ''; // Text before cursor at session start
  private textAfterStart = ''; // Text after cursor at session start
  private finalizedText = ''; // Accumulating string of final segments
  private interimText = ''; // Current interim phrase

  constructor(private logger: LoggerService) {
    this.logger.debug('TextStateManager initialized');
  }

  initialize(nativeElement: HTMLTextAreaElement | HTMLInputElement, formControl: FormControl): void {
    this.cleanup();
    this.nativeElement = nativeElement;
    this.formControl = formControl;
  }

  startVoiceSession(): VoiceSessionContext {
    if (this.voiceSessionActive) {
      this.endVoiceSession();
    }

    if (!this.nativeElement) {
      throw new Error('Native element not initialized');
    }

    this.voiceSessionActive = true;
    const currentState = this.captureCurrentState();
    this.sessionStartState = { ...currentState };

    this.textBeforeStart = currentState.text.substring(0, currentState.cursorPosition);
    this.textAfterStart = currentState.text.substring(currentState.cursorPosition);

    this.finalizedText = '';
    this.interimText = '';

    // console.log('🔧 Voice session started:', {
    //     cursor: currentState.cursorPosition,
    //     textBefore: this.textBeforeStart.slice(-20),
    //     textAfter: this.textAfterStart.slice(0, 20),
    // });

    return {
      sessionId: Date.now().toString(),
      startState: { ...this.sessionStartState },
    };
  }

  /**
   * Updates the temporary interim text.
   */
  updateInterimText(transcript: string): void {
    if (!this.voiceSessionActive) return;
    this.interimText = transcript;
    this.updateDisplay();
  }

  /**
   * Commits a finalized text segment.
   */
  commitFinalText(transcript: string): void {
    if (!this.voiceSessionActive) return;

    const trimmedTranscript = transcript.trim();
    if (trimmedTranscript) {
      if (this.finalizedText) {
        this.finalizedText += ' ' + trimmedTranscript;
      } else {
        this.finalizedText = trimmedTranscript;
      }
    }
    this.interimText = ''; // Clear interim text
    this.updateDisplay(true); // isFinalCommit = true
  }

  /**
   * Replaces the client-side assembled text with the server's full authoritative transcript.
   */
  replaceWithFullTranscript(transcript: string): void {
    if (!this.voiceSessionActive) return;
    this.finalizedText = transcript.trim();
    this.interimText = '';
    this.updateDisplay(true); // isFinalCommit = true
  }

  isVoiceSessionActive(): boolean {
    return this.voiceSessionActive;
  }

  getDisplayText(): string {
    return this.buildDisplayText();
  }

  endVoiceSession(): void {
    this.voiceSessionActive = false;
    this.sessionStartState = null;
    this.textBeforeStart = '';
    this.textAfterStart = '';
    this.finalizedText = '';
    this.interimText = '';
    // console.log('🔧 Voice session ended');
  }

  cleanup(): void {
    this.endVoiceSession();
    this.nativeElement = null;
    this.formControl = null;
  }

  private captureCurrentState(): TextState {
    if (!this.nativeElement) {
      throw new Error('Native element not initialized');
    }
    return {
      text: this.nativeElement.value || '',
      cursorPosition: this.nativeElement.selectionStart || 0,
      selectionEnd: this.nativeElement.selectionEnd || 0,
      timestamp: Date.now(),
    };
  }

  private buildDisplayText(): string {
    if (!this.voiceSessionActive) {
      return this.nativeElement?.value || '';
    }

    let assembledText = this.finalizedText;
    if (this.interimText) {
      if (assembledText) {
        assembledText += ' ' + this.interimText;
      } else {
        assembledText = this.interimText;
      }
    }

    const { spacedText } = this.applySpacing(this.textBeforeStart, assembledText, this.textAfterStart);
    return this.textBeforeStart + spacedText + this.textAfterStart;
  }

  private updateDisplay(isFinalCommit: boolean = false): void {
    if (!this.nativeElement || !this.formControl) {
      return;
    }

    const newText = this.buildDisplayText();

    let assembledText = this.finalizedText;
    if (this.interimText) {
      if (assembledText) {
        assembledText += ' ' + this.interimText;
      } else {
        assembledText = this.interimText;
      }
    }

    const { cursorOffset } = this.applySpacing(this.textBeforeStart, assembledText, this.textAfterStart);
    const newCursorPosition = this.textBeforeStart.length + cursorOffset;

    this.nativeElement.value = newText;
    this.formControl.setValue(newText, { emitEvent: false });
    this.nativeElement.setSelectionRange(newCursorPosition, newCursorPosition);

    if (isFinalCommit) {
      try {
        this.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
        this.formControl.markAsDirty();
        this.formControl.markAsTouched();
      } catch (error) {
        this.logger.warn('An error occurred while dispatching an input event. This may be caused by a third-party browser extension.', {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }

  private applySpacing(before: string, voiceText: string, after: string): SpacingResult {
    let spacedText = voiceText;
    let leadingSpace = '';
    let trailingSpace = '';

    if (before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n') && voiceText.length > 0) {
      leadingSpace = ' ';
    }

    if (after.length > 0 && voiceText.length > 0 && !voiceText.endsWith(' ') && !voiceText.endsWith('\n') && !after.startsWith(' ')) {
      trailingSpace = ' ';
    }

    spacedText = leadingSpace + voiceText + trailingSpace;

    return {
      spacedText,
      cursorOffset: spacedText.length - trailingSpace.length,
      leadingSpaceAdded: !!leadingSpace,
      trailingSpaceAdded: !!trailingSpace,
    };
  }
}
